// Date filter for the report list: the dashboard's "Time range" control
// (Dashboard/DataLayout/DashboardControls.js) - same trigger, two-month
// calendar and quick ranges, and like it only future days are blocked.
// Controlled by dateFrom / dateTo (YYYY-MM-DD, "" for none), which the feed
// keeps in the URL. A half-picked range stays local until its end day is
// clicked, so the list doesn't refetch on the first click.

import { useEffect, useRef, useState } from "react";
import { format, parseISO, subDays } from "date-fns";
import {
  Calendar,
  RANGE_OPTIONS,
} from "../../Dashboard/DataLayout/DashboardControls";
import { t } from "../shared/trendReportShared";

const inputClass =
  "rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400";

const PRESET_ZH = {
  3: "近 3 天",
  5: "近 5 天",
  7: "近 1 週",
  14: "近 2 週",
  30: "近 1 個月",
  90: "近 3 個月",
  365: "近 1 年",
};

// parseISO reads YYYY-MM-DD as local midnight, so the day never shifts.
const formatDay = (iso, language) =>
  format(parseISO(iso), language === "zh" ? "yyyy年M月d日" : "MMM d, yyyy");

// "Last 1 week" = the 7 days ending today, as concrete dates so the URL and
// the backend only ever deal in from / to.
const presetRange = (days) => {
  const today = new Date();
  return {
    from: format(subDays(today, days - 1), "yyyy-MM-dd"),
    to: format(today, "yyyy-MM-dd"),
  };
};

export default function ReportDateFilter({
  dateFrom,
  dateTo,
  language,
  loading = false,
  onDateChange,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ startDate: dateFrom, endDate: dateTo });
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Each opening starts from the applied range; a half-picked one is dropped.
  const toggle = () => {
    if (!open) setDraft({ startDate: dateFrom, endDate: dateTo });
    setOpen((v) => !v);
  };

  const activePreset = RANGE_OPTIONS.find((o) => {
    const r = presetRange(o.days);
    return dateFrom === r.from && dateTo === r.to;
  });

  // Label shown on the trigger button.
  const triggerLabel = activePreset
    ? language === "zh"
      ? PRESET_ZH[activePreset.days]
      : activePreset.label
    : dateFrom || dateTo
      ? `${dateFrom ? formatDay(dateFrom, language) : "…"} → ${dateTo ? formatDay(dateTo, language) : "…"}`
      : t("allDates", language);

  const selectRange = (range) => {
    onDateChange(range);
    setOpen(false);
  };

  const handleCalendarChange = ({ startDate: s, endDate: e }) => {
    setDraft({ startDate: s, endDate: e });
    if (s && e) selectRange({ from: s, to: e }); // auto-close once a full range is picked
  };

  return (
    <div className="flex flex-wrap w-full justify-end items-center gap-4 mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
      <div
        className="relative flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"
        ref={rootRef}
      >
        {t("timeRange", language)}
        {/* Single trigger */}
        <button
          type="button"
          onClick={toggle}
          className={`${inputClass} flex items-center gap-2`}
        >
          {loading ? (
            <svg
              className="w-3.5 h-3.5 text-orange-500 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg
              className="w-3.5 h-3.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}
          {triggerLabel}
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {/* Two-pane popover: calendar (left) + preset list (right) */}
        {open && (
          <div className="absolute z-50 top-full mt-2 right-0 flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl">
            {/* Left pane — flight-ticket calendar */}
            <div className="p-4">
              <Calendar
                startDate={draft.startDate}
                endDate={draft.endDate}
                onChange={handleCalendarChange}
                language={language}
              />
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {draft.startDate
                    ? draft.endDate
                      ? `${formatDay(draft.startDate, language)}  →  ${formatDay(draft.endDate, language)}`
                      : `${formatDay(draft.startDate, language)}  →  ${t("pickEndDate", language)}`
                    : t("clickStartEnd", language)}
                </span>
                {(draft.startDate || draft.endDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setDraft({ startDate: "", endDate: "" });
                      onDateChange({ from: "", to: "" });
                    }}
                    className="text-orange-500 hover:text-orange-600 ml-4"
                  >
                    {t("clearDates", language)}
                  </button>
                )}
              </div>
            </div>

            {/* Right pane — preset ranges. "All dates" is extra: unlike the
                dashboard, the list's default is no range at all. */}
            <div className="w-36 shrink-0 border-l border-gray-100 dark:border-gray-700 p-2 flex flex-col gap-0.5">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {t("quickRanges", language)}
              </p>
              {[{ days: null }, ...RANGE_OPTIONS].map((o) => {
                const active = o.days
                  ? activePreset?.days === o.days
                  : !dateFrom && !dateTo;
                return (
                  <button
                    key={o.days ?? "all"}
                    type="button"
                    onClick={() =>
                      selectRange(
                        o.days ? presetRange(o.days) : { from: "", to: "" },
                      )
                    }
                    className={`text-left text-xs px-2 py-1.5 rounded-md transition-colors ${
                      active
                        ? "bg-orange-500 text-white font-semibold"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {!o.days
                      ? t("allDates", language)
                      : language === "zh"
                        ? PRESET_ZH[o.days]
                        : `Last ${o.label}`}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
